// src/context/PlayerContext.jsx
import { createContext, useState, useCallback, useRef } from 'react';
import youtubeService from '../pages/Youtubeservice';

export const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
    const [currentSong, setCurrentSong] = useState(null);
    const [playlist, setPlaylist] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showMiniPlayer, setShowMiniPlayer] = useState(false);
    const [showFullPlayer, setShowFullPlayer] = useState(false);

    const playerActionsRef = useRef(null);

    // ✅ Play a song and fetch related songs for Up Next
    // src/context/PlayerContext.jsx
    const playSong = useCallback(async (song, initialPlaylist = []) => {
        if (!song) return;

        // ✅ Pehle current song set karo
        setCurrentSong(song);
        setShowFullPlayer(true);
        setShowMiniPlayer(false);
        setIsPlaying(true);

        try {
            // ✅ Fetch YouTube related songs
            console.log('🔄 Fetching related songs for:', song.title);
            const relatedSongs = await youtubeService.getRelatedSongs(song.id, 20);

            if (relatedSongs && relatedSongs.length > 0) {
                // ✅ Current song + YouTube related songs
                const filtered = relatedSongs.filter(s => s.id !== song.id);
                const finalPlaylist = [song, ...filtered];
                console.log('✅ Related songs loaded:', finalPlaylist.length, 'songs');
                setPlaylist(finalPlaylist);  // 👈 Yeh important hai
            } else {
                // Fallback to initial playlist
                console.log('⚠️ No related songs, using initial playlist');
                const fallback = initialPlaylist.length > 0
                    ? [song, ...initialPlaylist.filter(s => s.id !== song.id)]
                    : [song];
                setPlaylist(fallback);
            }
        } catch (error) {
            console.error('❌ Error fetching related songs:', error);
            const fallback = initialPlaylist.length > 0
                ? [song, ...initialPlaylist.filter(s => s.id !== song.id)]
                : [song];
            setPlaylist(fallback);
        }
    }, []);

    // Play next song in playlist
    const playNext = useCallback((nextSong) => {
        if (nextSong) {
            setCurrentSong(nextSong);
            setIsPlaying(true);
        } else if (playlist.length > 0) {
            const currentIndex = playlist.findIndex(s => s.id === currentSong?.id);
            const nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
            setCurrentSong(playlist[nextIndex]);
            setIsPlaying(true);
        }
    }, [currentSong, playlist]);

    // Play previous song in playlist
    const playPrevious = useCallback((prevSong) => {
        if (prevSong) {
            setCurrentSong(prevSong);
            setIsPlaying(true);
        } else if (playlist.length > 0) {
            const currentIndex = playlist.findIndex(s => s.id === currentSong?.id);
            const prevIndex = currentIndex <= 0 ? playlist.length - 1 : currentIndex - 1;
            setCurrentSong(playlist[prevIndex]);
            setIsPlaying(true);
        }
    }, [currentSong, playlist]);

    // Toggle play/pause
    const togglePlayPause = useCallback(() => {
        if (isPlaying) {
            playerActionsRef.current?.pause();
        } else {
            playerActionsRef.current?.play();
        }
        setIsPlaying(prev => !prev);
    }, [isPlaying]);

    // Close mini player completely
    const closeMiniPlayer = useCallback(() => {
        playerActionsRef.current?.pause();
        setIsPlaying(false);
        setShowMiniPlayer(false);
        setShowFullPlayer(false);
        setCurrentSong(null);
        setPlaylist([]);
    }, []);

    return (
        <PlayerContext.Provider value={{
            // State
            currentSong,
            playlist,
            isPlaying,
            showMiniPlayer,
            showFullPlayer,
            selectedSong: currentSong, // Alias for easy access

            // Setters
            setCurrentSong,
            setPlaylist,
            setIsPlaying,
            setShowMiniPlayer,
            setShowFullPlayer,

            // Actions
            playSong,
            playNext,
            playPrevious,
            togglePlayPause,
            closeMiniPlayer,

            // Ref for player controls
            playerActionsRef,
        }}>
            {children}
        </PlayerContext.Provider>
    );
};